/**
 * Game hotkeys. One window-level listener while the game screen is mounted; ignored while typing
 * in a form field or while a modal overlay is open (except Esc, which closes it).
 */
import { useEffect } from 'react';
import { bus } from '../../game/bus';
import { type GameStoreState, gameStore, type MapTool } from '../../game/store';
import type { Translator } from '../../i18n';
import { getConnection } from '../../net/connection';
import { lobbyStore } from '../../net/lobby';
import { type HudPanel, hudStore } from './hudStore';

/** The hotkey card, the manual's table: key caps (in the reader's words) and what they do. */
export const hotkeys = (t: Translator): { keys: string[]; label: string }[] => [
  { keys: [t('key.space')], label: t('hk.pause') },
  { keys: ['1', '–', '5'], label: t('hk.speed') },
  { keys: ['M'], label: t('hk.move') },
  { keys: ['A'], label: t('hk.attack') },
  { keys: ['R'], label: t('hk.retreat') },
  { keys: ['H'], label: t('hk.hold') },
  { keys: ['F'], label: t('hk.focus') },
  { keys: ['Esc'], label: t('hk.esc') },
  { keys: ['P'], label: t('hk.production') },
  { keys: ['T'], label: t('hk.research') },
  { keys: ['I'], label: t('hk.intel') },
  { keys: ['W'], label: t('hk.met') },
  { keys: ['?', 'F1'], label: t('hk.card') },
  { keys: [t('key.rightClick')], label: t('hk.rightClick') },
  { keys: [t('key.shift'), t('key.click')], label: t('hk.shiftClick') },
  { keys: [t('key.arrows'), t('key.rightDrag')], label: t('hk.pan') },
  { keys: [t('key.wheel')], label: t('hk.zoom') },
];

/** Single player, or the host of a multiplayer room, may change speed and pause. */
export function canControlSpeed(): boolean {
  if (getConnection()?.kind === 'local') return true;
  const { room, playerId } = lobbyStore.getState();
  return !!room && room.hostId === playerId;
}

const TOOL_KEYS: Record<string, MapTool> = { m: 'move', a: 'attack', r: 'retreat' };
const PANEL_KEYS: Record<string, HudPanel> = { p: 'production', t: 'research', i: 'intel', w: 'weather' };

/** Hex of the first selected unit (camera focus, hold orders). */
export function selectionHex(g: GameStoreState): number {
  const id = g.selection[0];
  return g.view?.units.find((u) => u.id === id)?.hex ?? -1;
}

export function holdSelection(): void {
  const g = gameStore.getState();
  const hex = selectionHex(g);
  if (hex < 0) return;
  g.issueOrder('hold', hex);
  bus.emit('sound', { name: 'confirm' });
}

export function useGameHotkeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, select')) return;
      const g = gameStore.getState();
      const hud = hudStore.getState();
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      if (hud.overlay) {
        if (key === 'Escape') {
          hud.setOverlay(
            hud.overlay === 'manual' || hud.overlay === 'settings' || hud.overlay === 'surrender'
              ? 'menu'
              : null,
          );
          e.preventDefault();
        }
        return;
      }
      if (g.view?.winners) return;

      if (key === ' ') {
        e.preventDefault();
        if (canControlSpeed()) g.setPaused(!g.paused);
        return;
      }
      if (key >= '1' && key <= '5') {
        if (!canControlSpeed()) return;
        g.setSpeed(Number(key) as 1 | 2 | 3 | 4 | 5);
        if (g.paused) g.setPaused(false);
        return;
      }
      if (TOOL_KEYS[key]) {
        if (!g.selection.length) return;
        g.setTool(g.tool === TOOL_KEYS[key] ? 'select' : TOOL_KEYS[key]);
        bus.emit('sound', { name: 'click' });
        return;
      }
      if (PANEL_KEYS[key]) {
        hud.togglePanel(PANEL_KEYS[key]);
        bus.emit('sound', { name: 'paper' });
        return;
      }
      switch (key) {
        case 'h':
          holdSelection();
          return;
        case 'f': {
          const hex = selectionHex(g);
          const target =
            hex >= 0 ? hex : (g.view?.contacts.find((c) => c.unitId === g.inspectedContact)?.hex ?? -1);
          if (target >= 0) bus.emit('focusHex', { hex: target });
          return;
        }
        case '?':
        case 'F1':
          e.preventDefault();
          hud.setOverlay('hotkeys');
          return;
        case 'Escape':
          if (g.tool !== 'select') g.setTool('select');
          else if (hud.panel) hud.setPanel(null);
          else if (g.selection.length || g.inspectedContact !== null) {
            g.select([]);
            g.inspectContact(null);
          } else hud.setOverlay('menu');
          return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
